// JSON over HTTP to the API for the cloud-owned data (history, beans): same origin, the session cookie, every answer
// checked against its app-v1 schema; a network failure is the code "offline", an unreadable answer "malformed".
import { parseApiError, validate, type ApiError, type MessageSchemaName } from '@brewcore/app-contract';

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;
export type HttpResult<T> = { ok: true; value: T } | { ok: false; error: ApiError };

export interface HttpTarget {
  fetchImpl: FetchLike;
  base: string;
  headers: Record<string, string>;
}

export async function jsonRequest<T>(
  target: HttpTarget,
  method: string,
  path: string,
  body: unknown,
  schema: MessageSchemaName | null,
): Promise<HttpResult<T>> {
  let res: Response;
  try {
    res = await target.fetchImpl(`${target.base}${path}`, {
      method,
      credentials: 'same-origin',
      headers: { ...(body !== null ? { 'content-type': 'application/json' } : {}), ...target.headers },
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    return { ok: false, error: { code: 'offline' } };
  }
  if (res.status === 204) return { ok: true, value: null as T };
  const raw: unknown = await res.json().catch(() => null);
  if (!res.ok) return { ok: false, error: parseApiError(raw) };
  if (!schema) return { ok: true, value: raw as T };
  const parsed = validate(schema, raw);
  return parsed.ok ? { ok: true, value: parsed.value as T } : { ok: false, error: { code: 'malformed' } };
}
